import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell, faBox, faUsers, faSwimmingPool, faCalendarCheck,
  faCircleInfo, faCar, faPaw, faCheckDouble, faTrash,
  faRefresh, faFilter, faBullhorn, faTriangleExclamation, faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../utils/constants';

/* ── helpers ── */
const getUserId = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).id_usuario;
  } catch { return null; }
};

const getNavComponent = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).id_tipo_usuario;
  } catch { return null; }
};

const TIPO_META = {
  paquete:         { icon: faBox,                color: 'text-amber-600',  bg: 'bg-amber-100',  label: 'Paquete',    bar: 'bg-amber-500'  },
  visita:          { icon: faUsers,              color: 'text-blue-600',   bg: 'bg-blue-100',   label: 'Visita',     bar: 'bg-blue-500'   },
  reserva_piscina: { icon: faSwimmingPool,       color: 'text-cyan-600',   bg: 'bg-cyan-100',   label: 'Piscina',    bar: 'bg-cyan-500'   },
  reserva_salon:   { icon: faCalendarCheck,      color: 'text-violet-600', bg: 'bg-violet-100', label: 'Salón',      bar: 'bg-violet-500' },
  mascota:         { icon: faPaw,                color: 'text-green-600',  bg: 'bg-green-100',  label: 'Mascota',    bar: 'bg-green-500'  },
  vehiculo:        { icon: faCar,                color: 'text-slate-600',  bg: 'bg-slate-100',  label: 'Vehículo',   bar: 'bg-slate-500'  },
  sistema:         { icon: faCircleInfo,         color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Sistema',    bar: 'bg-indigo-500' },
  comunicado:      { icon: faBullhorn,           color: 'text-orange-600', bg: 'bg-orange-100', label: 'Comunicado', bar: 'bg-orange-500' },
  incidente:       { icon: faTriangleExclamation,color: 'text-red-600',    bg: 'bg-red-100',    label: 'Incidente',  bar: 'bg-red-500'    },
  alerta:          { icon: faShieldHalved,       color: 'text-rose-600',   bg: 'bg-rose-100',   label: 'Alerta',     bar: 'bg-rose-500'   },
  emergencia:      { icon: faTriangleExclamation,color: 'text-red-700',    bg: 'bg-red-200',    label: 'Emergencia', bar: 'bg-red-700'    },
};

const fmtDatetime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} · ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const timeAgo = (dateStr) => {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return 'hace un momento';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} días`;
  return fmtDatetime(dateStr);
};

const Snack = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const colors = { success: 'bg-green-500', error: 'bg-red-500', info: 'bg-blue-500' };
  return (
    <div className={`fixed top-5 right-5 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${colors[type] || colors.info}`}>
      <span>{msg}</span>
      <button onClick={onClose} className="ml-2 font-bold">✕</button>
    </div>
  );
};

export default function NotificationsPage({ NavigationComponent }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todas'); // 'todas' | 'no_leidas' | tipo key
  const [snack, setSnack] = useState({ msg: '', type: 'success' });
  const userId = getUserId();
  const prevCountRef = useRef(null);

  const showMsg = (msg, type = 'success') => setSnack({ msg, type });

  const fetchNotifs = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await axios.get(`${BASE_URL}/notificaciones/user/${userId}?limit=100`);
      const data = r.data;
      const unread = data.filter(n => !n.leida).length;
      // Avisar si llegan nuevas mientras la página está abierta
      if (prevCountRef.current !== null && unread > prevCountRef.current) {
        showMsg(`Tienes ${unread - prevCountRef.current} nueva(s) notificación(es)`, 'info');
      }
      prevCountRef.current = unread;
      setNotifs(data);
    } catch { /* silencioso en el poll */ }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => {
    fetchNotifs();
    const iv = setInterval(fetchNotifs, 15000);
    return () => clearInterval(iv);
  }, [fetchNotifs]);

  const markRead = async (id) => {
    try {
      await axios.put(`${BASE_URL}/notificaciones/${id}/read`);
      setNotifs(prev => prev.map(n => n.id_notificacion === id ? { ...n, leida: 1 } : n));
    } catch { showMsg('Error al marcar notificación', 'error'); }
  };

  const markAllRead = async () => {
    if (!userId) return;
    try {
      await axios.put(`${BASE_URL}/notificaciones/user/${userId}/read-all`);
      setNotifs(prev => prev.map(n => ({ ...n, leida: 1 })));
      showMsg('Todas marcadas como leídas');
    } catch { showMsg('Error al marcar notificaciones', 'error'); }
  };

  const deleteNotif = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/notificaciones/${id}`);
      setNotifs(prev => prev.filter(n => n.id_notificacion !== id));
    } catch { showMsg('Error al eliminar notificación', 'error'); }
  };

  const deleteRead = async () => {
    try {
      await axios.delete(`${BASE_URL}/notificaciones/user/${userId}/read`);
      setNotifs(prev => prev.filter(n => !n.leida));
      showMsg('Notificaciones leídas eliminadas');
    } catch { showMsg('Error al limpiar', 'error'); }
  };

  const filtered = notifs.filter(n => {
    if (filter === 'no_leidas') return !n.leida;
    if (filter !== 'todas') return n.tipo === filter;
    return true;
  });

  const unreadCount = notifs.filter(n => !n.leida).length;

  const meta = (tipo) => TIPO_META[tipo] || TIPO_META.sistema;

  /* Tabs de filtro */
  const filterTabs = [
    { key: 'todas',      label: 'Todas' },
    { key: 'no_leidas',  label: `No leídas${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
    ...Object.entries(TIPO_META).map(([key, m]) => ({ key, label: m.label })),
  ];

  if (loading) return (
    <>
      {NavigationComponent && <NavigationComponent />}
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-indigo-600"></div>
      </div>
    </>
  );

  return (
    <>
      {NavigationComponent && <NavigationComponent />}
      <Snack msg={snack.msg} type={snack.type} onClose={() => setSnack({ msg: '' })} />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow">
              <FontAwesomeIcon icon={faBell} className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800">Notificaciones</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {notifs.length} notificación(es) · {unreadCount} sin leer
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchNotifs}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors">
                <FontAwesomeIcon icon={faRefresh} className="h-4 w-4" />
              </button>
              {unreadCount > 0 && (
                <button onClick={markAllRead}
                  className="flex items-center gap-1 text-sm text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors border border-indigo-200">
                  <FontAwesomeIcon icon={faCheckDouble} className="h-4 w-4" />
                  Marcar todo leído
                </button>
              )}
              {notifs.some(n => n.leida) && (
                <button onClick={deleteRead}
                  className="flex items-center gap-1 text-sm text-red-500 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors border border-red-200">
                  <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                  Limpiar leídas
                </button>
              )}
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-2xl shadow p-4 mb-5">
            <div className="flex gap-2 flex-wrap">
              {filterTabs.map(tab => (
                <button key={tab.key} onClick={() => setFilter(tab.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                    ${filter === tab.key
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-300'}`}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lista */}
          <div className="space-y-3">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-16 text-center text-gray-400">
                <FontAwesomeIcon icon={faBell} className="h-12 w-12 mb-3 opacity-30" />
                <p className="text-lg font-medium">Sin notificaciones{filter !== 'todas' ? ' con ese filtro' : ''}</p>
              </div>
            ) : (
              filtered.map(n => {
                const m = meta(n.tipo);
                return (
                  <div key={n.id_notificacion}
                    className={`bg-white rounded-2xl shadow overflow-hidden transition-opacity ${n.leida ? 'opacity-75' : ''}`}>
                    <div className={`h-1 ${m.bar}`}></div>
                    <div className="p-4 flex items-start gap-4">
                      {/* Icono */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full ${m.bg} flex items-center justify-center mt-0.5`}>
                        <FontAwesomeIcon icon={m.icon} className={`h-4 w-4 ${m.color}`} />
                      </div>
                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-semibold ${n.leida ? 'text-gray-600' : 'text-gray-900'}`}>{n.titulo}</p>
                            {!n.leida && <span className="flex-shrink-0 w-2 h-2 bg-indigo-500 rounded-full"></span>}
                          </div>
                          <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.color}`}>
                            {m.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1 leading-relaxed">{n.mensaje}</p>
                        <p className="text-xs text-gray-400 mt-2">{timeAgo(n.fecha_creacion)}</p>
                      </div>
                      {/* Acciones */}
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {!n.leida && (
                          <button onClick={() => markRead(n.id_notificacion)}
                            className="p-1.5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Marcar como leída">
                            <FontAwesomeIcon icon={faCheckDouble} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => deleteNotif(n.id_notificacion)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar">
                          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      </div>
    </>
  );
}
